import React from "react";
import { connect } from "react-redux";
import update from "immutability-helper";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import {
  get,
  map,
  size,
  includes,
  toLower,
  filter,
  isEmpty,
  sortBy,
} from "lodash";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import { setBreadcrumb } from "../../../actions/common";
import { fetchProfiles, fetchLov, setFilters } from "../../../actions/profiles";
import Loader from "../../Common/Loader";
import ScrollableTable from "../../Common/Table/ScrollableTable";
import AddProfile from "../AddProfile/AddProfile";
import ProfilesSearch from "../ProfilesSearch";
import ProfileRow from "./ProfileRow";
import { getAttributeValue } from "../../../utils/profile";

const columns = [
  {
    name: "profiles_profileType",
    minWidth: 130,
    width: 130,
    filter: {
      type: "select",
      name: "profileType",
    },
  },
  {
    name: "profiles_profileName",
    minWidth: 300,
    width: 300,
    filter: {
      type: "smartsearch",
      name: "name",
      path: ["name"],
    },
  },
  {
    name: "profiles_tag",
    minWidth: 300,
    width: 300,
    filter: {
      type: "select",
      name: "tag",
    },
  },
  {
    name: "profiles_carrier",
    minWidth: 200,
    width: 200,
    filter: {
      type: "comboBox",
      name: "carrier",
    },
  },
  {
    name: "profiles_active",
    minWidth: 130,
    width: 130,
    filter: {
      type: "select",
      name: "active",
    },
  },
  {
    name: "profiles_note",
    minWidth: 60,
    width: 60,
  },
];

const styles = {
  mainContent: {
    maxHeight: "100%",
    overflow: "auto",
    maxWidth: "1500px",
  },
};

const ProfilesList = (props) => {
  const { classes, dispatch, language, profiles } = props;
  const [displayNb, setDisplayNb] = React.useState(30);
  const filters = get(profiles, ["filters"]);

  React.useEffect(() => {
    dispatch(setBreadcrumb([{ name: I18n.get("topbar_profiles") }]));
    dispatch(fetchProfiles());

    if (isEmpty(get(profiles, ["lov"]))) {
      dispatch(fetchLov());
    }

    if (isEmpty(get(profiles, ["filters"]))) {
      dispatch(
        setFilters({
          profileType: [],
          tag: [],
          carrier: [],
          name: "",
          active: [true],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshProfiles = () => {
    dispatch(fetchProfiles());
  };

  const handleFilterChange = (value, filterName) => {
    if (includes(value, "selectAll")) {
      value = map(get(get(profiles, ["lov"]), [filterName]), (opt) => opt.id);
    } else if (includes(value, "selectNone")) {
      value = [];
    } else if (value === null) {
      value = "";
    }
    dispatch(
      setFilters(
        update(filters, {
          [filterName]: { $set: value },
        })
      )
    );
  };

  const applyFilters = (item) => {
    let exclude = false;
    if (
      !isEmpty(get(filters, ["profileType"])) &&
      !includes(get(filters, ["profileType"]), get(item, ["type", "id"]))
    ) {
      exclude = true;
    }
    if (
      !isEmpty(get(filters, ["tag"])) &&
      !includes(get(filters, ["tag"]), get(item, ["tag", "id"]))
    ) {
      exclude = true;
    }
    if (
      !isEmpty(get(filters, ["carrier"])) &&
      !includes(
        get(filters, ["carrier"]),
        parseInt(getAttributeValue(item, 89))
      )
    ) {
      exclude = true;
    }
    if (
      get(filters, ["name"]) &&
      !includes(toLower(get(item, ["name"])), toLower(get(filters, ["name"])))
    ) {
      exclude = true;
    }
    if (
      get(filters, ["active"]) &&
      !includes(get(filters, ["active"]), get(item, ["active"]))
    ) {
      exclude = true;
    }
    return !exclude;
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="mainContent"
      style={{
        margin: "24px",
        padding: "24px",
        width: "auto",
        maxHeight: "calc(100vh - 148px)",
        minHeight: "unset",
        overflow: "auto",
      }}
    >
      <Grid item style={{ marginBottom: "24px" }}>
        <Grid container>
          <Grid item>
            <AddProfile
              language={language}
              onProfileCreated={refreshProfiles}
              defaultOpen={get(props, ["match", "params", "type"])}
            />
          </Grid>
          <Grid item>
            <ProfilesSearch />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Box className={classes.mainContent}>
          {get(profiles, ["isFetching"]) || isEmpty(get(profiles, ["lov"])) ? (
            <Loader height="calc(100vh - 256px)" noDecoration />
          ) : (
            <ScrollableTable
              columns={columns}
              hasMore={displayNb < size(get(profiles, ["profiles"]))}
              isFetching={get(profiles, ["isFetching"])}
              stickyHeader
              lazyLoading={true}
              fetchData={() => setDisplayNb(displayNb + 20)}
              containerHeight={"calc(100vh - 256px)"}
              filters={filters}
              lov={get(profiles, ["lov"])}
              handleFilterChange={handleFilterChange}
              data={get(profiles, ["profiles"])}
            >
              {map(
                filter(
                  sortBy(
                    filter(get(profiles, ["profiles"]), applyFilters),
                    (p) => toLower(get(p, ["name"]))
                  ),
                  (p, i) => i < displayNb
                ),
                (profile, index) => (
                  <ProfileRow
                    key={profile.id}
                    profile={profile}
                    index={index}
                    language={language}
                  />
                )
              )}
            </ScrollableTable>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
    profiles: state.profiles,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(ProfilesList));
